/**
 * Wire types for the two Packet-5 read-only check-in endpoints. These mirror
 * the backend Pydantic schemas byte-for-byte (schemas.py: CheckInQueueInsight*
 * and CheckInActivity*); if a field changes there, change it here — both the
 * Check-In page and the catch-up overview import from this one file so the
 * two surfaces cannot drift from each other.
 */

/** GET /api/check-ins/queue/insight — day-level half. Capacity-shaped fields
 * are null when no snapshot exists for the logical day (the queue was never
 * built, so there is nothing to measure). */
export interface CheckInQueueInsightQueue {
  snapshot_exists: boolean;
  snapshot_date: string;
  computed_at: string | null;
  batch_size: number | null;
  item_count: number | null;
  capacity: number | null;
  due_now_unanswered: boolean | null;
  suppressed_backfill_count: number | null;
  cutoff_hour: number;
  /** "missing_timezone" when the account has no IANA zone while at least one
   * active commitment exists — the otherwise-silent materialiser swallow. */
  commitments_suppressed_reason: string | null;
}

/** Why a habit is (not) offering catch-up items today. `reason` names the
 * first gate that fired, from the ratified vocabulary. */
export type CatchUpReason =
  | 'mode_none'
  | 'offered'
  | 'no_closed_period_yet'
  | 'no_debt_since_anchor'
  | 'all_periods_complete'
  | 'all_periods_answered'
  | 'nothing_outstanding'
  | 'no_capacity'
  | 'paused'
  | 'snoozed'
  | 'opaque_blocked';

export interface CheckInQueueInsightHabit {
  habit_id: number;
  title: string;
  backfill_mode: 'none' | 'retroactive_log' | 'debt_carry_forward';
  carryover_policy: 'stack' | 'merge';
  /** Effective scan bound with the 7/35-day defaults applied; null for mode none. */
  reach_days: number | null;
  anchor_date: string | null;
  owed_period_count: number;
  owed_total: number;
  offered_today_count: number;
  reason: CatchUpReason | string;
}

export interface CheckInQueueInsightResponse {
  queue: CheckInQueueInsightQueue;
  habits: CheckInQueueInsightHabit[];
}

/** GET /api/check-ins/activity — the check-in streak (finished-state: an
 * unopened day breaks, an empty day is neutral, an unfinished today never
 * breaks) plus checked-in counts per calendar bucket. */
export interface CheckInActivityToday {
  roster_size: number;
  answered: number;
  complete: boolean;
}

export interface CheckInActivityCheckedIn {
  day: number;
  week: number;
  month: number;
  quarter: number;
  year: number;
}

export interface CheckInActivityResponse {
  streak_current: number;
  today: CheckInActivityToday;
  checked_in: CheckInActivityCheckedIn;
}

export const CHECK_IN_INSIGHT_PATH = '/api/check-ins/queue/insight';
export const CHECK_IN_ACTIVITY_PATH = '/api/check-ins/activity';
